// src/storage/profileManager.ts — Multi-Profile & Tester Sandbox Manager (FR-PROF-001, FR-PROF-002)
// Provides transparent storage isolation for evaluators/parents testing the application
// so Tudor's real exam readiness statistics, streaks, and fatigue telemetry are never polluted.

export type UserProfile = 'tudor' | 'tester';

export interface ProfileInfo {
  id: UserProfile;
  name: string;
  badge: string;
  tagline: string;
  isSandbox: boolean;
}

export const PROFILES: Record<UserProfile, ProfileInfo> = {
  tudor: {
    id: 'tudor',
    name: 'Tudor',
    badge: '⭐ Year 4 · Tudor',
    tagline: 'Candidate Examination Record (Jan 2018 cohort)',
    isSandbox: false,
  },
  tester: {
    id: 'tester',
    name: 'Tester Sandbox',
    badge: '🧪 Tester Sandbox',
    tagline: 'Isolated Test Environment (Zero Metric Pollution)',
    isSandbox: true,
  },
};

const PROFILE_KEY = 'clawey_active_profile';
const TESTER_PREFIX = 'tester_';

let isPatched = false;
let activeProfile: UserProfile = 'tudor';
const listeners: Array<(profile: UserProfile) => void> = [];

// Retain references to native Storage methods
const rawGetItem = Storage.prototype.getItem;
const rawSetItem = Storage.prototype.setItem;
const rawRemoveItem = Storage.prototype.removeItem;
const rawKey = Storage.prototype.key;

export class ProfileManager {
  /**
   * Initializes the storage interception layer.
   * Safe to call multiple times (idempotent).
   */
  static init(): void {
    // Read persisted profile from raw storage
    try {
      const saved = rawGetItem.call(localStorage, PROFILE_KEY);
      if (saved === 'tester' || saved === 'tudor') {
        activeProfile = saved;
      } else {
        activeProfile = 'tudor';
      }
    } catch {
      activeProfile = 'tudor';
    }

    if (isPatched) return;

    // Transparently redirect localStorage operations when in tester mode
    Storage.prototype.getItem = function (this: Storage, key: string): string | null {
      const effectiveKey = ProfileManager.resolveKey(this, key);
      return rawGetItem.call(this, effectiveKey);
    };

    Storage.prototype.setItem = function (this: Storage, key: string, value: string): void {
      const effectiveKey = ProfileManager.resolveKey(this, key);
      rawSetItem.call(this, effectiveKey, value);
    };

    Storage.prototype.removeItem = function (this: Storage, key: string): void {
      const effectiveKey = ProfileManager.resolveKey(this, key);
      rawRemoveItem.call(this, effectiveKey);
    };

    isPatched = true;
  }

  /**
   * Resolves the storage key depending on active profile.
   * Leaves system profile keys and non-clawey keys unaffected.
   */
  static resolveKey(storage: Storage, key: string): string {
    if (storage !== localStorage) return key;
    if (key === PROFILE_KEY) return key;

    if (activeProfile === 'tester' && key.startsWith('clawey_')) {
      return `${TESTER_PREFIX}${key}`;
    }

    return key;
  }

  /**
   * Gets the active profile.
   */
  static getActiveProfile(): UserProfile {
    return activeProfile;
  }

  /**
   * Gets detailed profile information.
   */
  static getActiveProfileInfo(): ProfileInfo {
    return PROFILES[activeProfile];
  }

  /**
   * Switches the active profile and notifies listeners.
   */
  static setActiveProfile(profile: UserProfile): void {
    if (activeProfile === profile) return;

    activeProfile = profile;
    try {
      rawSetItem.call(localStorage, PROFILE_KEY, profile);
    } catch (e) {
      console.warn('Failed to persist active profile:', e);
    }

    // Dispatch custom window event
    window.dispatchEvent(
      new CustomEvent('clawey:profile-change', { detail: { profile } })
    );

    // Notify registered listeners
    for (const listener of listeners) {
      try {
        listener(profile);
      } catch (err) {
        console.error('Error in profile listener:', err);
      }
    }
  }

  /**
   * Subscribes to profile change events.
   */
  static onProfileChange(listener: (profile: UserProfile) => void): () => void {
    listeners.push(listener);
    return () => {
      const index = listeners.indexOf(listener);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    };
  }

  /**
   * Checks if currently in tester sandbox mode.
   */
  static isTester(): boolean {
    return activeProfile === 'tester';
  }

  /**
   * Counts how many keys are currently stored in the tester sandbox.
   */
  static getTesterKeyCount(): number {
    let count = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = rawKey.call(localStorage, i);
        if (key && key.startsWith(TESTER_PREFIX)) {
          count++;
        }
      }
    } catch {
      // In constrained environments
    }
    return count;
  }

  /**
   * Clears all sandbox test data without touching Tudor's records.
   */
  static clearTesterData(): number {
    const keysToRemove: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = rawKey.call(localStorage, i);
        if (key && key.startsWith(TESTER_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        rawRemoveItem.call(localStorage, key);
      }
    } catch (e) {
      console.warn('Failed to clear tester data:', e);
    }
    return keysToRemove.length;
  }

  /**
   * Unpatches Storage prototype (used primarily in tests to restore clean state).
   */
  static _restoreForTesting(): void {
    Storage.prototype.getItem = rawGetItem;
    Storage.prototype.setItem = rawSetItem;
    Storage.prototype.removeItem = rawRemoveItem;
    isPatched = false;
    activeProfile = 'tudor';
    listeners.length = 0;
  }
}
